import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { fmtMoney, fmtCompactMoney, fmtPct, fmtNumber, CHART_PALETTE, AGING_STYLES } from "../components/ops/opsUtils";
import {
  Banknote, Wallet, TrendingUp, Users, Briefcase, Activity, CalendarRange, Layers, Upload, Plus, AlertTriangle, ArrowUpRight,
} from "lucide-react";
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid,
  BarChart, Bar, PieChart, Pie, Cell, Legend,
} from "recharts";
import { Button } from "../components/ui/button";
import { DeploymentPeriodCard } from "../components/metrics/DeploymentPeriodCard";

export default function OperationsDashboard() {
  const [data, setData] = useState(null);
  const [atRisk, setAtRisk] = useState([]);
  const [alertsFeed, setAlertsFeed] = useState({ alerts: [], counts: { total: 0, high: 0 } });
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);

  useEffect(() => { load(); }, []);
  const load = async () => {
    setLoading(true);
    try {
      const [d, ar, al] = await Promise.all([
        api.get("/operations/dashboard"),
        api.get("/operations/at-risk?days_inactive=45&min_lifetime_volume=3000&limit=8").catch(() => ({ data: [] })),
        api.get("/operations/alerts").catch(() => ({ data: { alerts: [], counts: { total: 0, high: 0 } } })),
      ]);
      setData(d.data);
      setAtRisk(ar.data || []);
      setAlertsFeed(al.data || { alerts: [], counts: { total: 0, high: 0 } });
    } finally { setLoading(false); }
  };

  const onImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      await api.post("/operations/import/spreadsheet", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await load();
    } finally { setUploading(false); e.target.value = ""; }
  };

  if (loading && !data) return <div className="p-10 text-[#6B7280]" data-testid="dashboard-loading">Loading portfolio…</div>;
  if (!data) return null;

  const k = data.kpis;
  return (
    <div className="p-6 md:p-8 max-w-[1500px] mx-auto" data-testid="operations-dashboard">
      {/* Page header */}
      <div className="flex flex-wrap items-end justify-between gap-3 mb-6">
        <div>
          <div className="uppercase-label">Operations</div>
          <h1 className="font-display text-3xl md:text-4xl font-semibold tracking-tight text-[#0A2540]">Executive dashboard</h1>
          <div className="text-sm text-[#6B7280] mt-1.5">Live portfolio health · {fmtNumber(k.active_loans)} active loans · {fmtNumber(k.active_clients)} active clients</div>
        </div>
        <div className="flex items-center gap-2">
          <label className="inline-flex items-center gap-2 h-9 px-3 rounded-md border border-[#E5E7EB] bg-white text-sm text-[#374151] hover:border-[#0066FF] cursor-pointer transition-colors" data-testid="import-sheet-btn">
            <Upload className="w-3.5 h-3.5" /> {uploading ? "Importing…" : "Import spreadsheet"}
            <input type="file" accept=".xlsx" hidden onChange={onImport} disabled={uploading} />
          </label>
          <Link to="/admin/loans/new">
            <Button className="bg-[#0066FF] hover:bg-[#0052CC] text-white h-9" data-testid="add-funding-btn">
              <Plus className="w-3.5 h-3.5 mr-1.5" /> Add funding
            </Button>
          </Link>
        </div>
      </div>

      <div className="mb-3"><DeploymentPeriodCard data={data.deployment} /></div>

      {/* KPI grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-5 gap-3 mb-8">
        <KpiCard icon={Wallet}    label="Outstanding principal"  value={fmtMoney(k.total_outstanding_principal)} sub={k.unallocated_paydowns?.loans ? `principal advanced, active loans · ${fmtMoney(k.unallocated_paydowns.amount)} unallocated paydowns on ${k.unallocated_paydowns.loans} loan(s) not deducted` : "principal advanced, active loans"} testid="kpi-outstanding-principal" />
        <KpiCard icon={Banknote}  label="Current payoff"         value={fmtMoney(k.total_current_payoff)}        testid="kpi-current-payoff" accent />
        <KpiCard icon={TrendingUp} label="Accrued interest"      value={fmtMoney(k.total_accrued_interest)}      testid="kpi-accrued-interest" />
        <KpiCard icon={Activity}  label="ROI on active book"     value={fmtPct(k.roi_active)}  sub="(payoff + paydowns received − principal) ÷ principal" testid="kpi-roi-active" />
        <KpiCard icon={Layers}    label="Weighted avg rate"      value={`${(k.weighted_avg_rate * 100).toFixed(2)}%/mo`} sub="principal-weighted, active book" testid="kpi-weighted-rate" />

        <KpiCard icon={CalendarRange} label="Deployed MTD"       value={fmtMoney(k.funded_mtd)}                  testid="kpi-funded-mtd" />
        <KpiCard icon={CalendarRange} label="Deployed YTD"       value={fmtMoney(k.funded_ytd)}                  testid="kpi-funded-ytd" />
        <KpiCard icon={Banknote}  label="Deployed all-time"      value={fmtMoney(k.total_funded_all_time)}       testid="kpi-funded-alltime" />
        <KpiCard icon={Users}     label="Active clients"         value={fmtNumber(k.active_clients)}             sub={`${fmtNumber(k.active_loans)} loans${k.client_identity && k.client_identity.grouped_loans < k.client_identity.total_loans ? ` · ${k.client_identity.total_loans - k.client_identity.grouped_loans} ungrouped counted by name` : ""}`} testid="kpi-active-clients" />
        <KpiCard icon={Briefcase} label="Settled clients"        value={fmtNumber(k.settled_clients)}            sub={`${fmtNumber(k.settled_loans)} loans`} testid="kpi-settled-clients" />

        <KpiCard label="Avg advance"          value={k.average_advance != null ? fmtMoney(k.average_advance) : "—"} sub="all-time, actual advances" testid="kpi-avg-funding" />
        <KpiCard label="Avg months out"       value={`${k.avg_months_outstanding?.toFixed(1) || 0}`}              testid="kpi-avg-months" />
        <KpiCard label="Avg turnaround"       value={k.avg_turnaround_days != null ? `${k.avg_turnaround_days}d` : "—"} sub={k.turnaround_excluded_loans ? `${k.turnaround_excluded_loans} settled loans without a settlement date excluded` : null} testid="kpi-avg-turnaround" />
        <KpiCard label="Settlement recovery"  value={k.settlement_recovery_rate != null ? `${(k.settlement_recovery_rate*100).toFixed(0)}%` : "—"} sub={`realized, ${k.settlement_recovery_included_loans} loans${k.settlement_recovery_excluded_loans ? ` · ${k.settlement_recovery_excluded_loans} excluded (no settled amount)` : ""}`} testid="kpi-settlement-recovery" />
      </div>

      {/* Charts row 1 */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        <ChartCard title="Funding & settlements" subtitle="Last 12 months" className="lg:col-span-2" testid="chart-funding-trend">
          <ResponsiveContainer width="100%" height={280}>
            <AreaChart data={data.trends} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="fundedG" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#0066FF" stopOpacity={0.4} /><stop offset="100%" stopColor="#0066FF" stopOpacity={0} /></linearGradient>
                <linearGradient id="settledG" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#0A2540" stopOpacity={0.3} /><stop offset="100%" stopColor="#0A2540" stopOpacity={0} /></linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="2 2" stroke="#F3F4F6" vertical={false} />
              <XAxis dataKey="label" tick={{ fill: "#6B7280", fontSize: 11 }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fill: "#6B7280", fontSize: 11 }} axisLine={false} tickLine={false} tickFormatter={fmtCompactMoney} />
              <Tooltip contentStyle={{ background: "white", border: "1px solid #E5E7EB", borderRadius: 8, fontSize: 12 }} formatter={(v) => fmtMoney(v)} />
              <Area type="monotone" dataKey="funded" name="Funded" stroke="#0066FF" strokeWidth={2} fill="url(#fundedG)" />
              <Area type="monotone" dataKey="settled" name="Settled" stroke="#0A2540" strokeWidth={2} fill="url(#settledG)" />
            </AreaChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Active vs Settled" subtitle="Portfolio split" testid="chart-active-vs-settled">
          <ResponsiveContainer width="100%" height={280}>
            <PieChart>
              <Pie data={[
                  { name: "Active", value: data.status_distribution.active, fill: "#0066FF" },
                  { name: "Settled", value: data.status_distribution.settled, fill: "#0A2540" },
                  { name: "Pre-funding", value: data.status_distribution.pre_funding, fill: "#93C5FD" },
                ]} dataKey="value" innerRadius={60} outerRadius={100} paddingAngle={2}>
              </Pie>
              <Tooltip contentStyle={{ background: "white", border: "1px solid #E5E7EB", borderRadius: 8, fontSize: 12 }} />
              <Legend iconType="circle" wrapperStyle={{ fontSize: 12 }} />
            </PieChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      {/* Charts row 2 */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <ChartCard title="Portfolio aging" subtitle="Active loans by months outstanding" className="lg:col-span-2" testid="chart-aging">
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={data.aging_buckets.map((b) => ({
                bucket: AGING_STYLES[b.bucket]?.label || b.bucket,
                count: b.count, principal: b.principal,
                fill: AGING_STYLES[b.bucket]?.text || "#6B7280",
              }))} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="2 2" stroke="#F3F4F6" vertical={false} />
              <XAxis dataKey="bucket" tick={{ fill: "#6B7280", fontSize: 11 }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fill: "#6B7280", fontSize: 11 }} axisLine={false} tickLine={false} />
              <Tooltip contentStyle={{ background: "white", border: "1px solid #E5E7EB", borderRadius: 8, fontSize: 12 }} formatter={(v, name) => name === "principal" ? fmtMoney(v) : fmtNumber(v)} />
              <Bar dataKey="count" name="Loan count" radius={[4, 4, 0, 0]}>
                {data.aging_buckets.map((b, i) => <Cell key={i} fill={AGING_STYLES[b.bucket]?.text || CHART_PALETTE[i % CHART_PALETTE.length]} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Top attorneys" subtitle="By funded volume" testid="chart-attorney-leaderboard">
          <div className="space-y-2.5 mt-1">
            {data.attorney_leaderboard.slice(0, 8).map((a, i) => {
              const max = data.attorney_leaderboard[0]?.funded_volume || 1;
              const pct = (a.funded_volume / max) * 100;
              return (
                <Link key={a.attorney_email} to={`/admin/attorneys/${encodeURIComponent(a.attorney_email)}`} className="block group" data-testid={`leaderboard-row-${i}`}>
                  <div className="flex items-center justify-between text-xs mb-1">
                    <span className="text-[#0A2540] font-medium truncate group-hover:text-[#0066FF] transition-colors">{a.attorney_email}</span>
                    <span className="text-[#6B7280] tabular-nums">{fmtCompactMoney(a.funded_volume)}</span>
                  </div>
                  <div className="h-1.5 rounded-full bg-[#F3F4F6] overflow-hidden">
                    <div className="h-full rounded-full bg-gradient-to-r from-[#0066FF] to-[#60A5FA]" style={{ width: `${pct}%` }} />
                  </div>
                </Link>
              );
            })}
          </div>
        </ChartCard>
      </div>

      {/* Alerts feed */}
      {alertsFeed.counts.total > 0 && (
        <div className="bg-white border border-[#E5E7EB] rounded-lg p-5 mt-6" data-testid="alerts-widget">
          <div className="flex items-baseline justify-between mb-4">
            <div className="flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-[#B91C1C]" />
              <div className="font-medium text-[#0A2540]">Active alerts</div>
              <span className="text-xs text-[#6B7280]">Auto-detected portfolio signals</span>
            </div>
            <Link to="/admin/alerts" className="text-xs text-[#0066FF] hover:underline" data-testid="dashboard-view-all-alerts">
              View all {alertsFeed.counts.total} →
            </Link>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {alertsFeed.alerts.slice(0, 6).map((a) => (
              <Link
                key={a.id}
                to={a.related_payoff_id ? `/admin/payoffs/${a.related_payoff_id}` : a.related_loan_id ? `/admin/loans/${a.related_loan_id}` : a.related_client_name ? `/admin/clients/${encodeURIComponent(a.related_client_name)}` : "/admin/alerts"}
                className="block p-3 rounded-md border border-[#E5E7EB] hover:border-[#0066FF] transition-colors group"
                data-testid={`dashboard-alert-${a.id}`}
              >
                <div className="flex items-center gap-1.5 mb-1">
                  <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded font-medium" style={{ background: a.severity === "high" ? "#FEE2E2" : "#FEF9C3", color: a.severity === "high" ? "#B91C1C" : "#A16207" }}>{a.severity}</span>
                  <span className="text-[10px] uppercase tracking-wider text-[#9CA3AF]">{a.type.replace("_", " ")}</span>
                </div>
                <div className="text-sm font-medium text-[#0A2540] truncate group-hover:text-[#0066FF]">{a.title}</div>
                <div className="text-[11px] text-[#6B7280] mt-0.5 line-clamp-2">{a.summary}</div>
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* At-Risk relationships */}
      {atRisk.length > 0 && (
        <div className="bg-white border border-[#E5E7EB] rounded-lg p-5 mt-6" data-testid="at-risk-widget">
          <div className="flex items-baseline justify-between mb-4">
            <div className="flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-[#B45309]" />
              <div className="font-medium text-[#0A2540]">At-risk relationships</div>
              <span className="text-xs text-[#6B7280]">Attorneys who funded $3k+ but went quiet 45+ days</span>
            </div>
            <span className="text-xs text-[#6B7280]">{atRisk.length} flagged</span>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {atRisk.map((a, i) => (
              <Link key={a.attorney_email} to={`/admin/attorneys/${encodeURIComponent(a.attorney_email)}`} className="block p-3 rounded-md border border-[#E5E7EB] hover:border-[#B45309] transition-colors group" data-testid={`at-risk-card-${i}`}>
                <div className="flex items-start justify-between">
                  <div className="text-sm font-medium text-[#0A2540] truncate group-hover:text-[#B45309]">{a.attorney_email}</div>
                  <ArrowUpRight className="w-3.5 h-3.5 text-[#9CA3AF] flex-shrink-0" />
                </div>
                <div className="flex items-baseline gap-3 mt-2">
                  <span className="font-display text-lg text-[#0A2540] tabular-nums">{fmtCompactMoney(a.funded_volume)}</span>
                  <span className="text-[11px] text-[#6B7280] tabular-nums">{a.loan_count} loans · {a.days_since_last_funding}d quiet</span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function KpiCard({ icon: Icon, label, value, sub, testid, accent }) {
  return (
    <div className={`bg-white border border-[#E5E7EB] rounded-lg p-4 ${accent ? "border-[#0066FF]/30 bg-gradient-to-br from-white to-[#F0F6FF]" : ""}`} data-testid={testid}>
      <div className="flex items-center justify-between text-xs uppercase tracking-wider text-[#6B7280] mb-2">
        <span className="truncate">{label}</span>
        {Icon && <Icon className="w-3.5 h-3.5 text-[#9CA3AF] flex-shrink-0" />}
      </div>
      <div className="font-display text-2xl text-[#0A2540] tabular-nums leading-tight">{value}</div>
      {sub && <div className="text-xs text-[#6B7280] mt-1">{sub}</div>}
    </div>
  );
}

function ChartCard({ title, subtitle, children, className = "", testid }) {
  return (
    <div className={`bg-white border border-[#E5E7EB] rounded-lg p-5 ${className}`} data-testid={testid}>
      <div className="flex items-baseline justify-between mb-4">
        <div>
          <div className="font-medium text-[#0A2540]">{title}</div>
          {subtitle && <div className="text-xs text-[#6B7280] mt-0.5">{subtitle}</div>}
        </div>
      </div>
      {children}
    </div>
  );
}
